// tournament/staging.ts - effective-stack staging (deep → mid → short → push/fold) and the Harrington
// M-zone bands (ST1, BUILD_PLAN §ST1: "M-zone/effective-stack staging"). Pure functions of the
// effective stack in BB (and, for the zone, the M-ratio). The staging is what routes a preflop
// decision: at push/fold depth the tournament path consults the DT Nash charts (tournament/pushfold.ts);
// above it, regular open-raising (the cash charts + PF5 reconciliation) applies.
import { PUSHFOLD_STACK_BANDS } from '@postflop/schema';
/** The BB above which push/fold gives way to open-raising - the top of the DT push/fold chart domain
 *  (`PUSHFOLD_STACK_BANDS` tops out at 20bb). At or below this, jam-or-fold is the short-stack path. */
export const PUSH_FOLD_MAX_BB = 20;
/** Map an effective stack (in BB) to its stage. Boundaries: deep > 40, mid (25, 40], short (20, 25],
 *  pushFold ≤ 20. The short band is deliberately narrow - it is the "one more level and you're
 *  jamming" zone. */
export const stageForStack = (effBB) => {
    if (effBB > 40)
        return 'deep';
    if (effBB > 25)
        return 'mid';
    if (effBB > PUSH_FOLD_MAX_BB)
        return 'short';
    return 'pushFold';
};
/** True iff the effective stack is in the push/fold zone (≤ 20bb) - i.e. the DT Nash charts apply and
 *  the preflop decision is jam-or-fold rather than an open-raise. */
export const isPushFoldDepth = (effBB) => effBB <= PUSH_FOLD_MAX_BB;
export const mZone = (m) => {
    if (m >= 20)
        return 'green';
    if (m >= 10)
        return 'yellow';
    if (m >= 5)
        return 'orange';
    if (m >= 1)
        return 'red';
    return 'dead';
};
// ── Push/fold band snapping ───────────────────────────────────────────────────────────────────────
// The DT charts are keyed on the discrete ladder 3/5/8/10/12/15/20 bb. A live effective stack (e.g.
// 9.3bb) must snap to the band the chart is authored for. We snap to the NEAREST band by absolute
// distance, breaking ties toward the SHORTER (wider) band - the conservative choice, since being one
// band too wide is safer than being one band too tight when you are close to the boundary.
/** The numeric BB value of a push/fold band string ('10bb' → 10). */
export const bandBB = (band) => parseInt(band, 10);
/** Ascending numeric ladder of the push/fold bands, paired with their string key. */
const BANDS = PUSHFOLD_STACK_BANDS.map((b) => ({
    band: b,
    bb: bandBB(b),
})).sort((a, b) => a.bb - b.bb);
/**
 * Snap an effective stack (in BB) to the nearest authored push/fold band. Nearest by absolute
 * distance; a tie goes to the shorter (wider) band. Stacks above the top band clamp to it (20bb) and
 * below the bottom clamp to it (3bb) - callers gate on `isPushFoldDepth` before using the result, so a
 * clamp only ever means "use the widest/tightest authored chart".
 */
export const nearestPushFoldBand = (effBB) => {
    let best = BANDS[0];
    let bestDist = Math.abs(effBB - best.bb);
    for (const cand of BANDS.slice(1)) {
        const dist = Math.abs(effBB - cand.bb);
        // strictly-closer wins; an exact tie keeps the already-chosen (lower, wider) band since we iterate
        // ascending and only replace on a strict improvement.
        if (dist < bestDist) {
            best = cand;
            bestDist = dist;
        }
    }
    return best.band;
};
//# sourceMappingURL=staging.js.map